import React, { useState, useEffect } from 'react';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { useAuth } from '../contexts/AuthContext';
import { createSubscription, completeSubscription } from '../services/subscription';

const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || ''
);

// Dark tactical styling for Payment Element
const appearance = {
  theme: 'night' as const,
  variables: {
    colorPrimary: '#C17A4A',
    colorBackground: '#0A0A0A',
    colorText: '#E8E6E3',
    colorDanger: '#DC2626',
    fontFamily: 'Courier Prime, monospace',
    borderRadius: '4px',
    spacingUnit: '4px',
  },
};

interface PaymentFormProps {
  price: string;
  priceId: string;
  clientSecret: string;
  setupIntentId: string;
  customerId: string;
}

interface SubscriptionFormProps {
  price: string;
  priceId: string;
}

const PaymentForm: React.FC<PaymentFormProps> = ({ price, clientSecret, setupIntentId, customerId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message || 'Payment form validation failed');
        setLoading(false);
        return;
      }

      // Confirm the setup intent (for collecting payment method during trial)
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
      });

      if (confirmError) {
        setError(confirmError.message || 'Payment setup failed');
        setLoading(false);
        return;
      }

      if (!setupIntent || setupIntent.status !== 'succeeded') {
        setError('Setup failed. Please try again.');
        setLoading(false);
        return;
      }

      // Complete the subscription on backend
      const completeResponse = await completeSubscription(
        setupIntentId,
        customerId,
        user!.uid
      );

      console.log('✅ Subscription created:', completeResponse.subscriptionId);

      // ✅ UPDATE USER'S SUBSCRIPTION STATUS
      localStorage.setItem('subscriptionActive', 'true');
      localStorage.setItem('mockSubscriptionActive', 'true');
      
      // Get user data from localStorage or auth context
      const authToken = user?.uid || localStorage.getItem('authToken') || '';
      const userEmail = user?.email || localStorage.getItem('userEmail') || '';
      
      // Update user object in localStorage if using auth context
      if (user) {
        const users = JSON.parse(localStorage.getItem('users') || '[]');
        const userIndex = users.findIndex((u: any) => u.email === user.email);
        if (userIndex !== -1) {
          users[userIndex].subscriptionActive = true;
          users[userIndex].subscriptionId = completeResponse.subscriptionId;
          localStorage.setItem('users', JSON.stringify(users));
        }
        
        // Also update auth token storage
        localStorage.setItem('authToken', user.uid);
        localStorage.setItem('userEmail', user.email);
        localStorage.setItem('loginTimestamp', Date.now().toString());
      }

      // Debug logging
      console.log('=== SUBSCRIPTION SUCCESS DEBUG ===');
      console.log('Auth token:', authToken || localStorage.getItem('authToken'));
      console.log('User email:', userEmail || localStorage.getItem('userEmail'));
      console.log('Subscription active:', localStorage.getItem('subscriptionActive'));
      console.log('Current user:', user);
      console.log('=================================');

      // ✅ REDIRECT TO GAME
      const finalToken = authToken || localStorage.getItem('authToken') || '';
      const finalEmail = userEmail || localStorage.getItem('userEmail') || '';
      
      if (finalToken && finalEmail) {
        const gameUrl = `https://play.counterscripthq.com?token=${finalToken}&email=${encodeURIComponent(finalEmail)}&sub=true`;
        console.log('Redirecting to:', gameUrl);
        window.location.href = gameUrl;
      } else {
        console.error('Missing auth data:', { token: finalToken, email: finalEmail });
        alert('Subscription successful, but missing auth data. Please login again.');
        window.location.href = 'https://counterscripthq.com/app/login';
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      setLoading(false);
    }
  };

  if (!clientSecret) {
    return (
      <div className="card-tactical text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-copper border-t-transparent mx-auto mb-4"></div>
        <p className="text-text-secondary">Initializing payment form...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Trial Notice */}
      <div className="mb-6 p-4 border-2 border-copper" style={{ backgroundColor: 'rgba(193, 122, 74, 0.1)' }}>
        <h3 className="text-lg font-display font-bold text-copper mb-2">
          7-Day Free Trial
        </h3>
        <p className="text-text-secondary text-sm">
          Your card will be charged <strong className="text-white">$0.00 today</strong>. After 7 days, you'll be charged {price}/month. Cancel anytime during the trial to avoid charges.
        </p>
      </div>

      {error && (
        <div className="bg-alert-red bg-opacity-10 border-2 border-alert-red p-4">
          <p className="text-alert-red text-sm font-mono">{error}</p>
        </div>
      )}

      <div className="card-tactical">
        <h3 className="text-xl font-display font-bold text-white mb-4">
          Payment Information
        </h3>
        <PaymentElement />
      </div>

      <div className="card-tactical bg-black">
        <div className="flex justify-between items-center mb-4">
          <span className="text-text-secondary font-mono text-sm">Plan:</span>
          <span className="text-white font-display font-semibold">
            COUP EDU — Educator Access
          </span>
        </div>
        <div className="flex justify-between items-center mb-2">
          <span className="text-text-secondary font-mono text-sm">Amount Today:</span>
          <span className="text-2xl font-display font-bold text-scan-green">
            $0.00
          </span>
        </div>
        <div className="flex justify-between items-center mb-2">
          <span className="text-text-secondary font-mono text-sm">After Trial:</span>
          <span className="text-xl font-display font-bold text-copper">
            {price}/month
          </span>
        </div>
        <div className="border-t-2 border-black pt-3 mt-3">
          <p className="text-text-secondary text-sm text-center font-mono mb-2">
            <span className="text-scan-green font-semibold">7-day free trial</span> • Credit card required
          </p>
          <p className="text-text-muted text-xs text-center font-mono">
            Your card will be charged automatically after the 7-day trial ends unless you cancel before then.
          </p>
        </div>
      </div>

      <button
        type="submit"
        disabled={loading || !stripe || !elements}
        className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <span className="flex items-center justify-center">
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Processing...
          </span>
        ) : (
          'Start 7-Day Free Trial'
        )}
      </button>

      <p className="text-text-muted text-xs text-center font-mono">
        By clicking "Start 7-Day Free Trial", you agree to be charged {price}/month after your trial ends unless you cancel.
      </p>
    </form>
  );
};

const SubscriptionForm: React.FC<SubscriptionFormProps> = ({ price, priceId }) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [setupIntentId, setSetupIntentId] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const { user } = useAuth();

  // Pre-create setup intent to get client secret
  useEffect(() => {
    if (!user || !user.email || !user.firstName || !user.lastName) {
      setError('User information incomplete. Please complete your profile.');
      return;
    }

    const initialize = async () => {
      try {
        setError('');
        const response = await createSubscription(
          user.uid,
          user.email,
          user.firstName!,
          user.lastName!
        );
        setClientSecret(response.clientSecret);
        setSetupIntentId(response.setupIntentId);
        setCustomerId(response.customerId);
      } catch (err: any) {
        setError(err.message || 'Failed to initialize subscription');
        console.error('Failed to initialize subscription:', err);
      }
    };

    initialize();
  }, [user]);

  if (error && !clientSecret) {
    return (
      <div className="card-tactical text-center py-12">
        <div className="bg-alert-red bg-opacity-10 border-2 border-alert-red p-4 mb-4">
          <p className="text-alert-red text-sm font-mono">{error}</p>
        </div>
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div className="card-tactical text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-copper border-t-transparent mx-auto mb-4"></div>
        <p className="text-text-secondary">Preparing payment form...</p>
      </div>
    );
  }

  const options: StripeElementsOptions = {
    clientSecret,
    appearance,
  };

  if (!setupIntentId || !customerId) {
    return (
      <div className="card-tactical text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-copper border-t-transparent mx-auto mb-4"></div>
        <p className="text-text-secondary">Preparing payment form...</p>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      <PaymentForm 
        price={price} 
        priceId={priceId} 
        clientSecret={clientSecret} 
        setupIntentId={setupIntentId}
        customerId={customerId}
      />
    </Elements>
  );
};

export default SubscriptionForm;

